import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../services/api/axios.config";
import { notify } from "../utils/methods/notification";

const initialState = {
  coaFormCompleteList: {},
  coaFormCreateResponse: null,
  coaFormFailResponse: null,
  coaFormDetails: null,
  testList: [],
  isLoading: false,
  isCreating: false,
  isFailing: false,
  isLoadingDetails: false,
  isLoadingTestList: false,
  highest_cycle: null,
  qc_has_na_cycle: null,
};
const getCycleNumber = (cycle) => {
    const match = cycle?.toString().match(/\d+/);
    return match ? Number(match[0]) : 0;
};

const getHighestCycle = (currentCycle, nextCycle) => {
    if (!nextCycle) return currentCycle;
    return getCycleNumber(nextCycle) > getCycleNumber(currentCycle) ? nextCycle : currentCycle;
};
// For Fetching Approved LSM Listing
export const getCoaFormCompleteList = createAsyncThunk(
  "bmr/getCoaFormCompleteList",
  async (filters, { rejectWithValue }) => {
    try {
      const response = await api.post(`/coa-form/complete/list`, filters);

      return response.data;
    } catch (error) {
     notify.error(
          error || error.message || error.response || error.response.data
        )
      return rejectWithValue(
        error.response ? error.response.data : error.message
      );
    }
  }
);

// For Fetching COA Form Details by ID
export const getCoaFormDetailsById = createAsyncThunk(
  "bmr/getCoaFormDetailsById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/coa-form/${id}/details`);
      return response.data;
    } catch (error) {
     notify.error(
          error || error.message || error.response || error.response.data
        )
      return rejectWithValue(
        error.response ? error.response.data : error.message
      );
    }
  }
);

// For Fetching Test List for Dropdown
export const getTestList = createAsyncThunk(
  "bmr/getTestList",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(`/coa-form/test/list`);
      return response.data;
    } catch (error) {
     notify.error(
          error || error.message || error.response || error.response.data
        )
      return rejectWithValue(
        error.response ? error.response.data : error.message
      );
    }
  }
);

// For Creating COA Form
export const createCoaForm = createAsyncThunk(
  "bmr/createCoaForm",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await api.post(`/coa-form/create`, formData);
      notify.success("COA form created successfully!");
      return response.data;
    } catch (error) {
      notify.error(error || error.message || error.response || error.response.data);
      return rejectWithValue(
        error.response ? error.response.data : error.message
      );
    }
  }
);

// For Failing COA Form Under Quality Control
export const failCoaFormUnderQualityControl = createAsyncThunk(
  "bmr/failCoaFormUnderQualityControl",
  async (args, { rejectWithValue }) => {
    try {
      const { formData, id } = args
      const response = await api.post(`/coa-form/fail/under-quality-control/${id}`, formData);
      return response.data;
    } catch (error) {
     notify.error(
          error || error.message || error.response || error.response.data
        )
      return rejectWithValue(
        error.response ? error.response.data : error.message
      );
    }
  }
);


// For QC Approval of COA Form
export const approveQualityControl = createAsyncThunk(
  "bmr/approveQualityControl", // Fixed: match slice name
  async (args, { rejectWithValue }) => {
    try {
      const { formData, id } = args
      const response = await api.put(`/coa-form/qc/approved/${id}`, formData);
      return response.data;
    } catch (error) {
     notify.error(error?.response?.data || error?.message || error)
      return rejectWithValue(
        error.response ? error.response.data : error.message
      );
    }
  }
);


const qualityControlSlice = createSlice({
  name: "qualityControlList",
  initialState,
  reducers: {
    // Clear create response
    clearCoaFormCreateResponse: (state) => {
      state.coaFormCreateResponse = null;
    },
    // Clear fail response
    clearCoaFormFailResponse: (state) => {
      state.coaFormFailResponse = null;
    },
    // Clear form details
    clearCoaFormDetails: (state) => {
      state.coaFormDetails = null;
    },
    // Clear test list
    clearTestList: (state) => {
      state.testList = [];
    }
  },
  extraReducers: (builder) =>
    builder
      // Handling the state case for the LSM Approved listing
      .addCase(getCoaFormCompleteList.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getCoaFormCompleteList.fulfilled, (state, action) => {
        // Structure the data to match what the component expects
        state.coaFormCompleteList = {
          items: action.payload?.data ?? [],
          pageDetails: action.payload?.pageDetails ?? {
            total_count: 0,
            page: 1,
            per_page: 10
          }
        };
        state.qc_has_na_cycle = action.payload?.data?.has_na_cycle ?? null;
        const highestCycle = action.payload.data?.highest_cycle ?? action.payload.highest_cycle ?? null;
        state.highest_cycle = getHighestCycle(state.highest_cycle, highestCycle); 
        state.isLoading = false;
      })
      .addCase(getCoaFormCompleteList.rejected, (state) => {
        state.isLoading = false;
      })

      // Handling COA Form Details by ID
      .addCase(getCoaFormDetailsById.pending, (state) => {
        state.isLoadingDetails = true;
      })
      .addCase(getCoaFormDetailsById.fulfilled, (state, action) => {
        state.coaFormDetails = action.payload?.data ?? null;
        state.isLoadingDetails = false;
      })
      .addCase(getCoaFormDetailsById.rejected, (state) => {
        state.isLoadingDetails = false;
      })

      // Handling Test List
      .addCase(getTestList.pending, (state) => {
        state.isLoadingTestList = true;
      })
      .addCase(getTestList.fulfilled, (state, action) => {
        state.testList = action.payload?.data ?? [];
        state.isLoadingTestList = false;
      })
      .addCase(getTestList.rejected, (state) => {
        state.isLoadingTestList = false;
      })

      // Handling the state case for COA Form creation
      .addCase(createCoaForm.pending, (state) => {
        state.isCreating = true;
      })
      .addCase(createCoaForm.fulfilled, (state, action) => {
        state.coaFormCreateResponse = action.payload;
        state.isCreating = false;
      })
      .addCase(createCoaForm.rejected, (state) => {
        state.isCreating = false;
      })

      // Handling the state case for approve QualityControl Form 
      .addCase(approveQualityControl.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(approveQualityControl.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(approveQualityControl.rejected, (state) => {
        state.isLoading = false;
      })

      // Handling the state case for COA Form failure
      .addCase(failCoaFormUnderQualityControl.pending, (state) => {
        state.isFailing = true;
      })
      .addCase(failCoaFormUnderQualityControl.fulfilled, (state, action) => {
        state.coaFormFailResponse = action.payload;
        state.isFailing = false;
      })
      .addCase(failCoaFormUnderQualityControl.rejected, (state) => {
        state.isFailing = false;
      })

});

export const {
  clearCoaFormCreateResponse,
  clearCoaFormFailResponse,
  clearCoaFormDetails,
  clearTestList
} = qualityControlSlice.actions;

export const selectQCIsLoading = (state) => state.qualityControlList.isLoading;

export const selectcoaFormCompleteList = (state) => state.qualityControlList.coaFormCompleteList;
export const selectCoaFormCreateResponse = (state) => state.qualityControlList.coaFormCreateResponse;
export const selectCoaFormFailResponse = (state) => state.qualityControlList.coaFormFailResponse;
export const selectCoaFormDetails = (state) => state.qualityControlList.coaFormDetails;
export const selectTestList = (state) => state.qualityControlList.testList;
export const selectIsCreating = (state) => state.qualityControlList.isCreating;
export const selectIsFailing = (state) => state.qualityControlList.isFailing;
export const selectIsLoadingDetails = (state) => state.qualityControlList.isLoadingDetails;
export const selectIsLoadingTestList = (state) => state.qualityControlList.isLoadingTestList;
export const selectHighestCycle = (state) => state.qualityControlList.highest_cycle;
export const selectQcHasNacycle = (state) => state.qualityControlList.qc_has_na_cycle;

export default qualityControlSlice.reducer;